<script lang="ts">
  import type { GroupedTransaction } from "../../../api/transactions";
  import { ISSUE_TYPES } from "../../../helpers/constants";
  import { t } from "../../../helpers/translations";
  import {
    createGetRelatedTransactionsQuery,
    createGetRequestInvoiceDetailsQuery,
  } from "../../../queries/transactions";
  import IconButton from "../../buttons/IconButton.svelte";
  import UnlinkButton from "../../buttons/UnlinkButton.svelte";
  import SimpleRow from "../SimpleRow.svelte";
  import ConnectionsSection from "./ConnectionsSection.svelte";
  import SectionHeader from "./SectionHeader.svelte";
  import {
    canResolveTransactionManually,
    getEntityId,
    getTransactionDetailTableRows,
    getTransactionSubtitle,
    getTransactionTitle,
    showAsUnlinkedTransaction,
  } from "./data";

  export let transaction: GroupedTransaction;
  export let showConnections: boolean = true;
  export let onOpenTransaction: (relatedTransaction: GroupedTransaction) => void;
  export let onCloseTransaction: () => void;
  export let onAddConnection: () => void;
  export let onCloseConnections: () => void;
  export let onClose: (() => void) | undefined;
  export let onUnlinkTransaction: (() => void) | undefined = undefined;
  export let onResolveTransaction: (() => void) | undefined = undefined;

  const relatedTransactionsQuery = createGetRelatedTransactionsQuery(
    transaction.type,
    getEntityId(transaction),
    {
      transactions: transaction.related_transactions ?? [],
      resolved_transaction_details: {
        resolved: !!transaction.is_resolved,
        resolved_by: "",
        type: transaction.type,
      },
    },
    showConnections
  );

  // we have to do this because passing initialData to the query means isLoading will always be false
  $: isLoading =
    $relatedTransactionsQuery.isFetching && !$relatedTransactionsQuery.data.transactions.length;

  $: requestInvoiceDetailsQuery =
    transaction &&
    createGetRequestInvoiceDetailsQuery(
      transaction.type,
      transaction.supplier_id,
      transaction.reference
    );

  $: title = getTransactionTitle(transaction);
  $: subtitle = getTransactionSubtitle(transaction);
  $: detailFields = getTransactionDetailTableRows(transaction);
  $: connectionsEmptyStateTitle = showAsUnlinkedTransaction(transaction)
    ? t("connections.emptyState.title")
    : t("connections.emptyState.noOrders");
  $: issueOrOder = transaction.type === "order" || ISSUE_TYPES.includes(transaction.type);
  $: containerWide = issueOrOder;
  $: canResolveManually = canResolveTransactionManually(transaction);
</script>

<div class="container" class:container-wide={containerWide}>
  <div class="order-info">
    <div class="header">
      <SectionHeader {title} {subtitle} borderBottom={!$$slots.top}>
        <div class="actions">
          {#if onClose}
            <IconButton name="close" on:click={onClose} />
          {/if}
          {#if onUnlinkTransaction}
            <UnlinkButton onClick={onUnlinkTransaction} />
          {/if}
          <slot name="header-actions" />
        </div>
      </SectionHeader>
    </div>

    <div class="main">
      <slot name="top" />

      <div class="detail-table" class:border-top={issueOrOder}>
        {#each detailFields as field}
          <SimpleRow {...field} />
        {/each}
      </div>

      <slot name="middle" />

      {#if showConnections}
        <div class="connections" class:border-bottom={transaction.type === "order"}>
          <ConnectionsSection
            {canResolveManually}
            {isLoading}
            emptyStateTitle={connectionsEmptyStateTitle}
            resolveTooltipTitle={transaction.type === "order"
              ? t("tooltip.resolveOrder.title")
              : t("tooltip.resolveInvoice.title")}
            relatedTransactions={$relatedTransactionsQuery.data.transactions}
            resolvedTransactionDetails={$relatedTransactionsQuery.data.resolved_transaction_details}
            requestInvoiceDetails={$requestInvoiceDetailsQuery.data?.request_details}
            {onOpenTransaction}
            {onCloseTransaction}
            {onAddConnection}
            {onCloseConnections}
            {onResolveTransaction}
          />
        </div>
      {/if}

      <slot name="footer" />
    </div>
  </div>
</div>

<style>
  .container {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    width: 480px;
    height: 100%;
    background-color: var(--rekki-light);
    border-radius: 16px;
  }

  .container-wide {
    width: 620px;
  }

  .border-top {
    border-top: solid 1px var(--rk-divider-0);
  }

  .border-bottom {
    border-bottom: solid 1px var(--rk-divider-0);
  }

  @media screen and (min-width: 1440px) {
    .container {
      width: 560px;
    }

    .container-wide {
      width: 720px;
    }
  }

  .header {
    margin-top: 32px;
    padding: 0 32px;
  }

  .actions {
    display: flex;
    flex-direction: row-reverse;
  }

  .main {
    overflow: scroll;
    padding: 0 32px 32px 32px;
  }

  .order-info {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: stretch;
    height: 100%;
  }

  .connections {
    margin-top: 40px;
  }
</style>
